import { useContext, useEffect } from 'react';
import Draggable, { DraggableEventHandler } from 'react-draggable';
import { useAtom } from 'jotai';
import { PopoverHeader, PopoverBody } from '@src/components/popover-content/PopoverContent';
import { reactFlowInstanceAtom, getSubmenuStateAtom } from '@src/atoms';
import { CustomTooltip } from '@src/components/tooltip/Tooltip';
import { Icon } from '@src/components/icon/Icon';
import { Button } from '@/components/ui/button';
import { BEHAVIOR_RULES_NODE_INFO_WIDTH, NODE_INFO_WIDTH } from '@src/consts';
import {
  popoverCoordinatesAtom,
  selectedNodePropsAtom,
} from '@src/atoms/popover';
import {
  wrapperSizeAtom
} from '@src/atoms';
import { useToast } from "@/components/ui/use-toast"
import type { NodeToRenderData, } from '@src/components/custom-nodes/types';
import { IfStatementNodeInfo } from '@src/components/node-info/IfStatementNodeInfo';
import { BehaviorRulesNodeInfo } from '@src/components/node-info/BehaviorRulesNodeInfo.tsx';
import { GenericNodeInfo } from '@src/components/node-info/GenericNodeInfo';
import { SliderNodeInfo } from '@src/components/node-info/SliderNodeInfo';
import { ReactflowInstanceContext } from '@src/scenario-editor/ScenarioEditor';
import { deleteNode, updateNode, deleteStatementChildren } from '@src/scenario-editor/utils';
import cn from 'classnames';
import { useAllowDeletion } from '@src/components/node-info/hooks/useAllowDeletion.hook';

/**
 * LF360-1933 round 2: the default panel width, by node type.
 *
 * Keyed off nodeType here rather than written as `cardWidth` on the Behavior
 * Rules node factory, because `cardWidth` lives in node DATA: a value set there
 * only reaches nodes created after the change, so every behavior-rules node in
 * an already-saved scenario would keep opening at 395 while new ones opened at
 * 450 - the same panel at two widths, decided by the node's age.
 *
 * An explicit `cardWidth` still wins over this, so a per-node override remains
 * possible; this only moves what happens when nothing asked for a width.
 */
const defaultWidthByType = (nodeType: string): number =>
  nodeType === 'if:condition-behavior' ? BEHAVIOR_RULES_NODE_INFO_WIDTH : NODE_INFO_WIDTH;

const infoRendererByType = (nodeType: string) => {
  if (
    nodeType === 'if:condition'
  ) {
    return [
      'Condition',
      IfStatementNodeInfo
    ] as const;
  }

  if (
    nodeType === 'if:condition-behavior'
  ) {
    return [
      'Behavior rules',
      BehaviorRulesNodeInfo
    ] as const;
  }

  if (nodeType === 'message:slider') {
    return [
      'Slider',
      SliderNodeInfo
    ] as const;
  }

  return [
    'Node info',
    GenericNodeInfo,
  ] as const;
};

export const NodeInfo = ({ isPreview }: { isPreview: boolean }) => {
  const { toast } = useToast();

  const reactFlowInstance = useContext(ReactflowInstanceContext).get(reactFlowInstanceAtom)!;
  const [popoverCoordinates, setPopoverCoordinates] = useAtom(popoverCoordinatesAtom);
  const [selectedNodeProps, setSelectedNodeProps] = useAtom(selectedNodePropsAtom);
  const [wrapperSize] = useAtom(wrapperSizeAtom);
  const [, setSubmenuOpened] = useAtom(getSubmenuStateAtom);

  console.log('>>> selectedNodeProps', selectedNodeProps);

  useEffect(() => {
    return () => {
      setSelectedNodeProps(null);
    }
  }, []);

  useEffect(() => {
    setSubmenuOpened(false);
  }, [selectedNodeProps]);

  const selectedNodePropsData = selectedNodeProps?.data as NodeToRenderData;
  const sectionWidth = selectedNodePropsData?.cardWidth ?? defaultWidthByType(selectedNodePropsData?.nodeType ?? '');
  const sectionWidthNumber = Number.parseInt(sectionWidth.toString(), 10);
  const [title, InfoRenderer] = infoRendererByType(selectedNodePropsData?.nodeType ?? '');
  const deletionPredicate = selectedNodePropsData?.deletionPredicate;

  // Use the new useAllowDeletion hook
  const [allowDeletion] = useAllowDeletion(selectedNodeProps!, deletionPredicate);
  console.log('>>> allowDeletion', allowDeletion);

  if (!reactFlowInstance) {
    return null;
  }

  const defaultCoordinates = {
    x: popoverCoordinates.x ?? wrapperSize.x - sectionWidthNumber,
    y: popoverCoordinates.y ?? 0,
  }
  const dragHandler: DraggableEventHandler = (_,data) => {
    setPopoverCoordinates({ x: data.x, y: data.y });
  }
  const deleteItem = deleteNode({ reactFlowInstance });
  const deleteStatementWithChildrenItem = deleteStatementChildren({ reactFlowInstance });

  // setHistoryNavigationFlag(false);
  const updateItem = updateNode({ reactFlowInstance });
  const deleteItemFunc = ['if:condition', 'if:condition-behavior'].includes(selectedNodePropsData?.nodeType ?? '')
    ? deleteStatementWithChildrenItem
    : deleteItem;
  const deleteItemHandler = () => {
    if (!allowDeletion) {
      toast({
        title: 'Cannot delete node',
        description: deletionPredicate?.violationMessage,
        variant: 'destructive',
      })
      return;
    }

    const { success, message } = deleteItemFunc(selectedNodeProps?.id ?? '') ?? {};

    if (success) {
      setSelectedNodeProps(null);
    } else {
      toast({
        title: 'Cannot delete node',
        description: message ?? '',
        variant: 'destructive',
      })
    }
  }
  const closePopoverHandler = () => {
    const { success } = updateItem?.(selectedNodeProps?.id ?? '', {}, { selected: false }) ?? {};

    if (success) {
      setSelectedNodeProps({ ...selectedNodeProps!, selected: false });
    }
  }

  if (!selectedNodeProps?.selected) {
    return null;
  }

  return (
    <Draggable
      bounds={{
        top: 0,
        left: 0,
        right: wrapperSize.x - sectionWidthNumber,
        bottom: wrapperSize.y - 55
      }}
      onDrag={dragHandler}
      handle=".popover-header"
      position={defaultCoordinates}
    >
      <div
        className={`fixed bg-white shadow-lg rounded-md w-${sectionWidth} info-menu`}
        data-testid="node-info"
        style={{
          transform: `translate(${popoverCoordinates.x}px, ${popoverCoordinates.y}px)`,
        }}
      >
        <PopoverHeader
          className="popover-header cursor-move"
          title={selectedNodePropsData?.title ?? title}
          actions={
            <>
              <CustomTooltip content="Copy node ID">
                <Button
                  className='mr-2'
                  variant="headerInfoButton"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(selectedNodeProps?.id ?? '')
                      .catch(() => {
                        toast({
                          title: 'Cannot copy node ID',
                          description: 'Please try again',
                          variant: 'destructive',
                        });
                      });
                    toast({
                      title: 'ID is copied',
                      description: selectedNodeProps?.id ?? '',
                      duration: 2000,
                    });
                  }}
                  data-testid="info=>copy-node-id"
                >
                  Copy ID
                </Button>
              </CustomTooltip>
              {!isPreview && (
                <CustomTooltip content="Delete node">
                  <button
                    className="mr-2"
                    tabIndex={-1}
                    onClick={deleteItemHandler}
                    data-testid="info=>delete-node"
                    disabled={allowDeletion === undefined || isPreview}
                  >
                    <Icon type="delete" />
                  </button>
                </CustomTooltip>
              )}
              <CustomTooltip content="Close info">
                <button tabIndex={-1} onClick={closePopoverHandler} >
                  <Icon type="close" />
                </button>
              </CustomTooltip>
            </>
          }
        />
        <PopoverBody>
          <div className={
            cn({
              // INFO: hide overflow for the main info window
              // 'h-0 overflow-hidden': isSubmenuOpened,
            })
          }>
            {InfoRenderer && (
              <InfoRenderer
                key={selectedNodeProps.id}
                selectedNodeProps={selectedNodeProps}
                disabled={isPreview}
                isPreview={isPreview}
              />
            )}
          </div>
        </PopoverBody>
      </div>
  </Draggable>
  )
}
